<template>
  <div>
    <b-modal
      id="modal-multi-1"
      v-model="showModal"
      no-close-on-esc
      no-close-on-backdrop
      ok-only
      no-stacking
      centered
      hide-header
    >
      <p class="title">{{ $t('invoices.esign.nps.modalTitle') }}</p>
      <label v-if="score" class="label">{{ scoreLabel }}</label>
      <div class="nps">
        <b-button
          class="numbers"
          :class="{ selected: i === score }"
          @click="handleScore(i)"
          v-for="i in 10"
          :key="i"
          ><img
            src="@/assets/images/common/transparent-check.svg"
            class="success-icon"
            v-if="score === i"
          />
          <span v-else>{{ i }}</span>
        </b-button>
      </div>
      <div class="label-container">
        <label>{{ $t('invoices.esign.nps.label1') }}</label>
        <label>{{ $t('invoices.esign.nps.label2') }}</label>
      </div>
      <template slot="modal-footer">
        <div class="d-flex align-items-center">
          <p class="page-number">{{ $t('invoices.esign.nps.pageNumber1') }}</p>
          <b-button
            :disabled="!score"
            variant="secondary"
            size="sm"
            v-b-modal.modal-multi-2
            class="text-uppercase"
            >{{ $t('invoices.esign.nps.btnNext') }}</b-button
          >
        </div>
      </template>
    </b-modal>

    <b-modal
      id="modal-multi-2"
      title="Second Modal"
      no-close-on-esc
      no-close-on-backdrop
      ok-only
      centered
      hide-header
      ref="modal-multi-2"
    >
      <p class="title">{{ npsQuestions }}</p>
      <div>
        <b-textarea
          :placeholder="$t('invoices.esign.nps.placeholder')"
          class="text-box"
          v-model="text"
          required
        />
      </div>
      <template slot="modal-footer">
        <div class="d-flex align-items-center">
          <p class="page-number">{{ $t('invoices.esign.nps.pageNumber2') }}</p>
          <b-button
            variant="secondary"
            size="sm"
            @click.prevent="submitNPSScore"
            :disabled="loading || text.trim().length === 0"
            class="text-uppercase"
            >{{ $t('invoices.esign.nps.btnSubmit') }}</b-button
          >
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import npsServices from '@/services/nps'

export default {
  name: 'nps-modal',
  props: {
    showModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      score: null,
      text: '',
      loading: false,
      submitScore: false
    }
  },
  computed: {
    isDisabled() {
      return !this.submitScore
    },
    npsQuestions() {
      return this.score > 8
        ? this.$t('invoices.esign.nps.ques2')
        : this.$t('invoices.esign.nps.ques1')
    },
    scoreLabel() {
      const nps = this.$t('invoices.esign.nps')
      if (this.score < 7) {
        return nps.wnr
      } else if (this.score >= 7 && this.score < 9) {
        return nps.mr
      } else if (this.score >= 9 && this.score < 11) {
        return nps.dr
      }
      return ''
    }
  },
  methods: {
    handleScore(score) {
      this.score = score
      this.$mpTrack('NPS', {
        label: `Score Clicked - ${score}`
      })
    },
    submitNPSScore() {
      this.loading = true
      return npsServices
        .postNetPromotorScoreResponses({
          nps_response: {
            account_id: this.$store.state.account.accountId,
            survey_id: this.$store.getters.npsSurveyId,
            score: this.score,
            user_id: this.$store.getters.user.id,
            comment: this.text
          }
        })
        .then(() => {
          this.loading = false
          this.$mpTrack('NPS', {
            label: 'Score & Comment Submitted',
            score: this.score,
            comment: this.text
          })
          this.hideModal()
        })
        .catch((err) => {
          this.$mpTrack('NPS', {
            label: 'Score Not Submitted',
            err: err
          })
          this.loading = false
          this.hideModal()
        })
    },
    hideModal() {
      this.$emit('close')
      this.$refs['modal-multi-2'].hide()
      this.$notify({
        title: 'Thank you',
        text: 'Feedback Submitted Successfully.',
        type: 'success',
        duration: 3000
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables';
.nps {
  display: flex;
  justify-content: space-around;
  margin-top: 1.25rem;
  margin-bottom: 1rem;
}
.page-number {
  opacity: 0.6;
  font-family: $montserrat;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.7px;
  color: $lighter-text;
  margin: 0 1rem 0 0;
}

.title {
  text-transform: uppercase;
  font-family: $montserrat;
  font-size: 0.875rem;
  font-weight: bold;
  letter-spacing: 0.6px;
  text-align: left;
  color: $secondary;
  margin-bottom: 1.5rem;
}
.numbers {
  display: inline-block;
  width: 2.8rem;
  height: 2.8rem;
  font-weight: bold;
  border-radius: 50%;
  padding: 0;
  margin: 2px;
  background-color: $white;
  border: 2px solid $advanced-color !important;
  color: $advanced-color;
  box-shadow: none !important;

  &.selected,
  &:active,
  &:focus {
    background: $advanced-color !important;
  }

  &:nth-child(n + 7) {
    border-color: $pending-color !important;
    color: $pending-color;

    &.selected,
    &:active,
    &:focus {
      background: $pending-color !important;
    }
  }

  &:nth-child(n + 9) {
    border-color: $approved-color !important;
    color: $approved-color;

    &.selected,
    &:active,
    &:focus {
      background: $approved-color !important;
    }
  }
}
.label {
  border: 1px solid $secondary;
  color: $secondary;
  text-align: center;
  padding: 0.5rem;
  border-radius: 0.625rem;
}
.text-box {
  height: 8.875rem;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.16);
  border: 1px solid $table-border-color;
  resize: none;
}
.success-icon {
  font-size: 1.5rem;
  color: white;
}
.label-container {
  display: flex;
  justify-content: space-between;
}
</style>
